import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import {
  AccountSummary,
  getAdminAccounts,
  createAdminAccount,
  updateAdminAccount,
  deleteAdminAccount,
  generateSuggestedPassword,
  buildInviteShareText,
} from '../../services/adminAccounts';
import {
  ShieldCheck,
  UserPlus,
  Users,
  KeyRound,
  Sparkles,
  Copy,
  Check,
  Trash2,
  Lock,
  Unlock,
  RefreshCw,
  Search,
  AlertCircle,
  Clock,
  Layers,
  CheckCircle2,
} from 'lucide-react';

export interface AdminAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminAccountsModal: React.FC<AdminAccountsModalProps> = ({ isOpen, onClose }) => {
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form states
  const [nameInput, setNameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Last created / updated invite card
  const [recentInvite, setRecentInvite] = useState<{
    name: string;
    password: string;
  } | null>(null);

  // Reset password modal/dialog state
  const [resettingAccountId, setResettingAccountId] = useState<string | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Copy toast state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch accounts on modal open
  const loadAccounts = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const data = await getAdminAccounts();
      setAccounts(data);
    } catch (err: any) {
      setErrorMessage(err.message || 'Не вдалося завантажити список консультантів');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
      setPasswordInput(generateSuggestedPassword());
      setRecentInvite(null);
      setResettingAccountId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGeneratePassword = () => {
    setPasswordInput(generateSuggestedPassword());
  };

  const handleGenerateNewPassword = () => {
    setNewPasswordInput(generateSuggestedPassword());
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = nameInput.trim();
    const pwd = passwordInput.trim();

    if (!name || name.length < 2) {
      setErrorMessage("Введіть коректне ім'я консультанта (мінімум 2 символи)");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const res = await createAdminAccount(name, pwd || undefined);
    setIsSubmitting(false);

    if (res.success && res.account) {
      const usedPassword = res.initialPassword || pwd;
      setRecentInvite({
        name: res.account.name,
        password: usedPassword,
      });
      setNameInput('');
      setPasswordInput(generateSuggestedPassword());
      // Refresh list
      loadAccounts();
    } else {
      setErrorMessage(res.error || 'Помилка створення акаунта');
    }
  };

  const handleToggleActive = async (account: AccountSummary) => {
    const updatedStatus = !account.isActive;
    const res = await updateAdminAccount(account.id, { isActive: updatedStatus });
    if (res.success) {
      setAccounts((prev) =>
        prev.map((a) => (a.id === account.id ? { ...a, isActive: updatedStatus } : a))
      );
    } else {
      alert(res.error || 'Не вдалося змінити статус акаунта');
    }
  };

  const handleSaveNewPassword = async (account: AccountSummary) => {
    const pwd = newPasswordInput.trim();
    if (!pwd || pwd.length < 4) {
      alert('Пароль повинен містити мінімум 4 символи');
      return;
    }

    const res = await updateAdminAccount(account.id, { newPassword: pwd });
    if (res.success) {
      setRecentInvite({
        name: account.name,
        password: pwd,
      });
      setResettingAccountId(null);
      setNewPasswordInput('');
    } else {
      alert(res.error || 'Не вдалося оновити пароль');
    }
  };

  const handleDelete = async (account: AccountSummary) => {
    const confirmed = window.confirm(
      `Ви впевнені, що бажаєте видалити акаунт "${account.name}"?\nУсі збережені структури цього консультанта також будуть видалені.`
    );
    if (!confirmed) return;

    const res = await deleteAdminAccount(account.id);
    if (res.success) {
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
      if (recentInvite?.name === account.name) {
        setRecentInvite(null);
      }
    } else {
      alert(res.error || 'Не вдалося видалити акаунт');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    const handleSuccess = () => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2500);
    };

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(handleSuccess).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }

    function fallbackCopy(str: string) {
      try {
        const el = document.createElement('textarea');
        el.value = str;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        handleSuccess();
      } catch (err) {
        console.warn('Fallback copy error:', err);
      }
    }
  };

  const filteredAccounts = accounts.filter((a) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return a.name.toLowerCase().includes(q);
  });

  const activeCount = accounts.filter((a) => a.isActive).length;
  const blockedCount = accounts.length - activeCount;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-accent-neon" />
          <span>Керування консультантами та доступами</span>
        </div>
      }
      subtitle="Створення персональних акаунтів, генерація інвайтів та хмарне збереження структур"
    >
      <div className="space-y-6">
        {/* Error notification */}
        {errorMessage && (
          <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Section 1: Create New Consultant Account */}
        <div className="glass-panel rounded-2xl p-4 sm:p-5 border border-accent-emerald/30 bg-gradient-to-r from-emerald-950/20 via-slate-900/60 to-cyan-950/20 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-accent-neon" />
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                Створити новий доступ для консультанта
              </h4>
            </div>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-accent-neon font-mono">
              Хмарне KV сховище
            </span>
          </div>

          <form onSubmit={handleCreateAccount} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Name field */}
              <div>
                <label className="block text-[11px] font-semibold text-text-muted mb-1 uppercase tracking-wider">
                  Ім'я та прізвище консультанта
                </label>
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => {
                    setNameInput(e.target.value);
                    if (errorMessage) setErrorMessage(null);
                  }}
                  placeholder="наприклад, Олександр Коваленко"
                  className="
                    w-full px-3 py-2 text-sm rounded-xl bg-slate-950/80 border border-white/15
                    focus:border-accent-neon focus:ring-1 focus:ring-accent-neon/30 text-white
                    placeholder:text-text-muted focus:outline-none min-h-[40px]
                  "
                />
              </div>

              {/* Password field with generator */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-semibold text-text-muted uppercase tracking-wider">
                    Пароль доступу
                  </label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-[11px] text-accent-neon hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Згенерувати</span>
                  </button>
                </div>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Пароль"
                    className="
                      w-full pl-9 pr-3 py-2 text-sm font-mono rounded-xl bg-slate-950/80
                      border border-white/15 focus:border-accent-neon text-white
                      placeholder:text-text-muted focus:outline-none min-h-[40px]
                    "
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                variant="neon"
                size="sm"
                leftIcon={<UserPlus className="w-4 h-4" />}
                loading={isSubmitting}
                disabled={isSubmitting || !nameInput.trim()}
              >
                Створити доступ
              </Button>
            </div>
          </form>
        </div>

        {/* Recent Created / Updated Invite Card */}
        {recentInvite && (
          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-accent-emerald/40 space-y-3 animate-fadeIn">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-accent-neon font-bold text-xs">
                <CheckCircle2 className="w-4 h-4" />
                <span>Доступ для {recentInvite.name} готовий!</span>
              </div>
              <span className="text-[11px] text-text-muted">Персональний інвайт</span>
            </div>

            <div className="p-3 rounded-xl bg-slate-950/80 border border-white/10 font-mono text-xs text-text-secondary whitespace-pre-wrap">
              {buildInviteShareText(recentInvite.name, recentInvite.password)}
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <span className="text-[11px] text-text-muted">
                Пароль:{' '}
                <strong className="text-white font-mono bg-white/5 px-2 py-0.5 rounded">
                  {recentInvite.password}
                </strong>
              </span>
              <Button
                type="button"
                variant="primary"
                size="sm"
                leftIcon={
                  copiedId === 'recent' ? (
                    <Check className="w-4 h-4 text-slate-950" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )
                }
                onClick={() =>
                  copyToClipboard(
                    buildInviteShareText(recentInvite.name, recentInvite.password),
                    'recent'
                  )
                }
              >
                {copiedId === 'recent' ? 'Скопійовано!' : 'Скопіювати інвайт'}
              </Button>
            </div>
          </div>
        )}

        {/* Section 2: Consultant Accounts Directory */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-400" />
              <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                Зареєстровані консультанти ({accounts.length})
              </h4>
              <div className="flex items-center gap-1.5 text-[11px] ml-2">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {activeCount} акт.
                </span>
                {blockedCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">
                    {blockedCount} забл.
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 sm:w-64">
                <Search className="w-3.5 h-3.5 text-text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Пошук консультанта..."
                  className="
                    w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-slate-900/80 border border-white/10
                    focus:border-cyan-400 text-white placeholder:text-text-muted focus:outline-none min-h-[34px]
                  "
                />
              </div>

              {/* Refresh button */}
              <button
                type="button"
                onClick={loadAccounts}
                disabled={isLoading}
                className="
                  p-2 rounded-xl bg-surface-hover hover:bg-slate-700 text-text-secondary
                  hover:text-white border border-white/10 transition-colors cursor-pointer
                "
                title="Оновити список"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Accounts List */}
          <div className="space-y-2.5 max-h-[45vh] overflow-y-auto custom-scrollbar pr-1">
            {isLoading && accounts.length === 0 ? (
              <div className="p-8 text-center text-xs text-text-muted flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-accent-neon" />
                <span>Завантаження консультантів...</span>
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="glass-card rounded-2xl p-8 text-center border border-dashed border-slate-700 space-y-2">
                <Users className="w-8 h-8 text-text-muted mx-auto stroke-1" />
                <div className="text-xs font-semibold text-text-primary">
                  {searchQuery ? `Нічого не знайдено за запитом «${searchQuery}»` : 'Немає створених консультантів'}
                </div>
                <p className="text-[11px] text-text-muted max-w-sm mx-auto">
                  Створіть перший акаунт за допомогою форми вище, щоб надати персональний доступ консультанту.
                </p>
              </div>
            ) : (
              filteredAccounts.map((account) => {
                const formattedDate = new Date(account.createdAt).toLocaleDateString('uk-UA', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                });

                const isResettingThis = resettingAccountId === account.id;

                return (
                  <div
                    key={account.id}
                    className={`
                      glass-card rounded-2xl p-3.5 border transition-all space-y-3
                      ${
                        account.isActive
                          ? 'border-white/10 hover:border-white/20 bg-slate-900/60'
                          : 'border-rose-500/20 bg-rose-950/10'
                      }
                    `}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm text-white truncate">
                            {account.name}
                          </span>
                          <span
                            className={`
                              text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1
                              ${
                                account.isActive
                                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/25'
                                  : 'bg-rose-500/10 text-rose-400 border border-rose-500/25'
                              }
                            `}
                          >
                            {account.isActive ? (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                <span>Активний</span>
                              </>
                            ) : (
                              <>
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                                <span>Заблоковано</span>
                              </>
                            )}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 mt-1 text-[11px] text-text-muted flex-wrap font-mono">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-text-muted" />
                            <span>Створено: {formattedDate}</span>
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-cyan-300">
                            <Layers className="w-3 h-3" />
                            <span>{account.structureCount} збережених структур</span>
                          </span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
                        {/* Toggle active / block */}
                        <button
                          type="button"
                          onClick={() => handleToggleActive(account)}
                          className={`
                            px-2.5 py-1.5 rounded-xl text-xs font-medium border flex items-center gap-1.5 transition-colors cursor-pointer
                            ${
                              account.isActive
                                ? 'bg-surface-hover hover:bg-rose-500/20 text-text-secondary hover:text-rose-400 border-white/10 hover:border-rose-500/30'
                                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                            }
                          `}
                          title={account.isActive ? 'Заблокувати доступ' : 'Розблокувати доступ'}
                        >
                          {account.isActive ? (
                            <>
                              <Lock className="w-3.5 h-3.5 text-rose-400" />
                              <span className="hidden md:inline">Заблокувати</span>
                            </>
                          ) : (
                            <>
                              <Unlock className="w-3.5 h-3.5 text-emerald-400" />
                              <span>Розблокувати</span>
                            </>
                          )}
                        </button>

                        {/* Reset password button */}
                        <button
                          type="button"
                          onClick={() => {
                            if (isResettingThis) {
                              setResettingAccountId(null);
                            } else {
                              setResettingAccountId(account.id);
                              setNewPasswordInput(generateSuggestedPassword());
                            }
                          }}
                          className="
                            px-2.5 py-1.5 rounded-xl text-xs font-medium bg-surface-hover hover:bg-slate-700
                            border border-white/10 hover:border-amber-400/40 text-text-secondary hover:text-amber-300
                            transition-colors flex items-center gap-1.5 cursor-pointer
                          "
                          title="Змінити або скинути пароль"
                        >
                          <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                          <span className="hidden sm:inline">Змінити пароль</span>
                        </button>

                        {/* Copy link button */}
                        <button
                          type="button"
                          onClick={() => copyToClipboard('https://app.starlifeodesa.com', 'link_' + account.id)}
                          className="
                            px-2 py-1.5 rounded-xl text-xs font-medium bg-surface-hover hover:bg-slate-700
                            border border-white/10 hover:border-cyan-400/40 text-text-secondary hover:text-cyan-300
                            transition-colors flex items-center gap-1 cursor-pointer
                          "
                          title="Скопіювати посилання на сервіс"
                        >
                          {copiedId === 'link_' + account.id ? (
                            <Check className="w-3.5 h-3.5 text-accent-neon" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-cyan-400" />
                          )}
                          <span className="hidden lg:inline">{copiedId === 'link_' + account.id ? 'Скопійовано' : 'Посилання'}</span>
                        </button>

                        {/* Delete account */}
                        <button
                          type="button"
                          onClick={() => handleDelete(account)}
                          className="
                            p-2 rounded-xl text-text-muted hover:text-rose-400 hover:bg-rose-500/10
                            transition-colors cursor-pointer
                          "
                          title="Видалити акаунт"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Inline password reset input */}
                    {isResettingThis && (
                      <div className="pt-2 border-t border-white/10 flex flex-col sm:flex-row gap-2 items-center animate-fadeIn">
                        <div className="relative flex-1 w-full">
                          <KeyRound className="w-3.5 h-3.5 text-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={newPasswordInput}
                            onChange={(e) => setNewPasswordInput(e.target.value)}
                            placeholder="Новий пароль..."
                            className="
                              w-full pl-8 pr-20 py-1.5 text-xs font-mono rounded-xl bg-slate-950/90
                              border border-amber-400/40 text-white focus:outline-none
                            "
                          />
                          <button
                            type="button"
                            onClick={handleGenerateNewPassword}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-amber-400 hover:underline cursor-pointer"
                          >
                            Згенерувати
                          </button>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleSaveNewPassword(account)}
                          >
                            Зберегти
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setResettingAccountId(null)}
                          >
                            Скасувати
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
